/* eslint-disable max-len */
import { Button, Form, Table, Tooltip, Typography } from 'antd';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';
import routePathEnum from 'enums/routePathEnum';
import useProfily from 'hooks/useProfily';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import leiUctyApi, { ILeiDetail, ILeiPrehled, ILeiPrehledInput, leiDetailApi } from 'services/lei';
import zobrazitIco from 'styles/images/icon_detail_plus.png';
import getLanguageObject from 'utils/getLanguageObject';
import { validateICO } from 'utils/validations/icoValidation';
import { validateLEI } from 'utils/validations/leiValidation';
import DetailLei from './DetailLei';

const PrideleneLeiPrehled: React.FC = () => {
    const [form] = Form.useForm();
    const history = useHistory();
    const { setLevel } = useProfily();
    const Text = Typography.Text;
    const { language } = useContext(Context);
    const [pagination, setPagination] = useState<ILeiPrehledInput>({
        order: 'lei:asc',
        limit: 10,
        offset: 0
    });
    const [currentFilterData, setCurrentFilterData] = useState<{
        lei?: string;
        regauthentityid?: string;
        legalname?: string;
    }>();
    const [uctyData, setUctyData] = useState<ILeiPrehled>();
    const [detailUcet, setDetailUcet] = useState<ILeiDetail>();
    const [submitErr, setSubmitErr] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [show401Err, setShow401Err] = useState<boolean>(false);
    const { search } = useLocation();

    // nastavenie jazyka podla url params ?lang=en
    useEffect(() => {
        let langArr = search
            .substring(1)
            .split('&')
            .filter(e => e.includes('lang'));
        const lang = langArr.length ? langArr[0].split('=')[1] : '';

        if (lang !== language && (lang === 'cz' || lang === 'en')) {
            setLevel('language', lang);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    useEffect(() => {
        form.validateFields();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [submitErr]);

    useEffect(() => {
        if (uctyData) {
            setLoading(true);
            leiUctyApi({
                ...currentFilterData,
                ...pagination
            })
                .then(resp => {
                    setUctyData(resp);
                })
                .finally(() => setLoading(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pagination]);

    return (
        <LoadingWrapper loading={loading}>
            <h1
                style={{
                    color: '#006866',
                    fontWeight: 600,
                    marginTop: 10
                }}
                id="top"
            >
                <TranslateText>LEI_SEZNAM</TranslateText>
            </h1>
            <FormWrapper
                layoutPosition="horizontal"
                size="small"
                labelAlign="left"
                colon={false}
                outForm={form}
                onFinish={vals => {
                    console.log(vals);
                    const formatedData = Object.fromEntries(
                        Object.entries(vals).filter(
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            ([, v]: any) => v !== undefined && v !== ''
                        )
                    );
                    setCurrentFilterData(formatedData);
                    const callApi = () => {
                        setLoading(true);
                        leiUctyApi({
                            ...formatedData,
                            ...pagination
                        })
                            .then(resp => {
                                setUctyData(resp);
                            })
                            .catch(err => err.response.status === 404 && setShow401Err(true))
                            .finally(() => setLoading(false));
                    };

                    const objkeys = Object.keys(formatedData);
                    objkeys.includes('lei') || objkeys.includes('regauthentityid') || objkeys.includes('legalname')
                        ? callApi()
                        : setSubmitErr(true);
                }}
            >
                {!uctyData && (
                    <>
                        <InputComponent
                            onChange={() => {
                                setShow401Err(false);
                                setSubmitErr(false);
                            }}
                            label="LEI"
                            name="lei"
                            type="text"
                            rules={[
                                {
                                    required: false
                                },
                                () => ({
                                    validator(_, value: string) {
                                        if (submitErr)
                                            return Promise.reject(
                                                <TranslateText>DUVERNE_INFO_NEPOVINNE_ERR</TranslateText>
                                            );
                                        if (!value || /^\d{10}$/.test(value) || validateLEI(value) === 0)
                                            return Promise.resolve();

                                        return Promise.reject(<TranslateText>NEVALIDNI_LEI_VS</TranslateText>);
                                    }
                                })
                            ]}
                        />
                        <InputComponent
                            onChange={() => {
                                setShow401Err(false);
                                setSubmitErr(false);
                            }}
                            label="DETAILLEI_REGISTAUTHORITYENTITYID"
                            name="regauthentityid"
                            type="text"
                            rules={[
                                {
                                    required: false
                                },
                                () => ({
                                    validator(_, value: string) {
                                        if (submitErr)
                                            return Promise.reject(
                                                <TranslateText>DUVERNE_INFO_NEPOVINNE_ERR</TranslateText>
                                            );
                                        if (!value || validateICO(value) === 0) return Promise.resolve();
                                        return Promise.reject(<TranslateText>NEVALIDNI_ICO</TranslateText>);
                                    }
                                })
                            ]}
                        />
                        <InputComponent
                            onChange={() => {
                                setShow401Err(false);
                                setSubmitErr(false);
                            }}
                            label="DETAILLEI_LEGALNAME"
                            name="legalname"
                            type="text"
                            maxLength={500}
                            rules={[
                                {
                                    required: false
                                },
                                () => ({
                                    validator() {
                                        if (submitErr)
                                            return Promise.reject(
                                                <TranslateText>DUVERNE_INFO_NEPOVINNE_ERR</TranslateText>
                                            );
                                        return Promise.resolve();
                                    }
                                })
                            ]}
                        />
                    </>
                )}
                {uctyData && !detailUcet && (
                    <>
                        <Title
                            style={{
                                color: '#006866'
                            }}
                            level={5}
                            text="LEI_SEZNAM_VYBRANYCH"
                        />
                        <Table
                            size="small"
                            bordered={true}
                            columns={[
                                {
                                    ellipsis: true,
                                    title: (
                                        <Paragraph
                                            text="LEI"
                                            style={{
                                                fontWeight: 600
                                            }}
                                        />
                                    ),
                                    key: 'lei',
                                    dataIndex: 'lei',
                                    width: 200
                                },
                                {
                                    ellipsis: true,
                                    title: (
                                        <Paragraph
                                            text="DETAILLEI_LEGALNAME"
                                            style={{
                                                fontWeight: 600
                                            }}
                                        />
                                    ),
                                    key: 'legalname',
                                    dataIndex: 'legalname'
                                },
                                {
                                    ellipsis: true,
                                    title: (
                                        <Paragraph
                                            text="DETAILLEI_REGISTAUTHORITYENTITYID"
                                            style={{
                                                fontWeight: 600
                                            }}
                                        />
                                    ),
                                    key: 'regauthentityid',
                                    dataIndex: 'regauthentityid'
                                },
                                {
                                    ellipsis: {
                                        showTitle: false
                                    },
                                    title: (
                                        <Paragraph
                                            text="DETAILLEI_ENTITYSTATUS"
                                            style={{
                                                fontWeight: 600
                                            }}
                                        />
                                    ),
                                    key: 'registrationstatus',
                                    dataIndex: 'registrationstatus',
                                    width: 160,
                                    render: registrationstatus => (
                                        <Tooltip
                                            mouseEnterDelay={0.5}
                                            overlayClassName="tableTooltip"
                                            placement="bottomRight"
                                            title={getLanguageObject(language)[`LEI_GLEIF_STAV_${registrationstatus}`]}
                                        >
                                            {getLanguageObject(language)[`LEI_GLEIF_STAV_${registrationstatus}`]}
                                        </Tooltip>
                                    )
                                },
                                {
                                    ellipsis: {
                                        showTitle: false
                                    },
                                    title: (
                                        <Paragraph
                                            text="DETAILLEI_REGISTRATIONSTATUS"
                                            style={{
                                                fontWeight: 600
                                            }}
                                        />
                                    ),
                                    key: 'entitystatus',
                                    dataIndex: 'entitystatus',
                                    width: 100,
                                    render: entitystatus => (
                                        <Tooltip
                                            mouseEnterDelay={0.5}
                                            overlayClassName="tableTooltip"
                                            placement="bottomRight"
                                            title={getLanguageObject(language)[`LEI_STATUS_${entitystatus}`]}
                                        >
                                            {getLanguageObject(language)[`LEI_STATUS_${entitystatus}`]}
                                        </Tooltip>
                                    )
                                },
                                {
                                    ellipsis: true,
                                    title: (
                                        <Paragraph
                                            text="LEI_DATUM_ZMENY"
                                            style={{
                                                fontWeight: 600
                                            }}
                                        />
                                    ),
                                    key: 'lastupdatedate',
                                    dataIndex: 'lastupdatedate',
                                    render: registrationstatus => moment(registrationstatus).format('DD.MM.YYYY HH:mm')
                                },
                                {
                                    dataIndex: 'edit',
                                    key: 'edit',
                                    width: 40,
                                    render: () => (
                                        <a>
                                            <img src={zobrazitIco} width="22px" />
                                        </a>
                                    )
                                }
                            ]}
                            rowKey={(_, i) => i || 0}
                            dataSource={uctyData?.seznamlei}
                            rowClassName={(_, i) => (i % 2 ? 'tableRowGray' : '')}
                            pagination={{
                                total: uctyData?.count,
                                onChange: page =>
                                    setPagination(pv => {
                                        return {
                                            ...pv,
                                            offset: (page - 1) * 10
                                        };
                                    }),
                                current: uctyData.offset / 10 + 1,
                                showSizeChanger: false,
                                defaultPageSize: 10
                            }}
                            onRow={row => {
                                return {
                                    onClick: () => {
                                        setLoading(true);
                                        leiDetailApi(row.oid)
                                            .then(resp => setDetailUcet(resp))
                                            .finally(() => setLoading(false));
                                    }
                                };
                            }}
                            locale={{
                                emptyText: <TranslateText>ZADNE_UDAJE</TranslateText>
                            }}
                        />
                    </>
                )}
                {detailUcet && <DetailLei data={detailUcet} />}
                {uctyData && (
                    <Button
                        type="default"
                        id="1"
                        name="1"
                        style={{
                            marginTop: 6
                        }}
                        htmlType="button"
                        onClick={() => {
                            document.getElementById('top')?.scrollIntoView();
                            detailUcet
                                ? setDetailUcet(undefined)
                                : (setUctyData(undefined),
                                  setPagination({
                                      order: 'lei:asc',
                                      limit: 10,
                                      offset: 0
                                  }));
                        }}
                    >
                        <TranslateText>ZPET</TranslateText>
                    </Button>
                )}
                {!uctyData && (
                    <>
                        <Button
                            type="primary"
                            id="2"
                            name="2"
                            htmlType="submit"
                            style={{
                                marginTop: 16
                            }}
                        >
                            <TranslateText>VYHLEDAT</TranslateText>
                        </Button>
                        {show401Err ? (
                            <Text className="extramessage-text">
                                <TranslateText>RESPONSE_ERROR_404</TranslateText>
                            </Text>
                        ) : null}
                    </>
                )}
                {!uctyData && (
                    <>
                        <div
                            style={{
                                display: 'block',
                                textDecoration: 'underline',
                                marginTop: 6
                            }}
                        >
                            <a onClick={() => history.push(routePathEnum.LEI_POPIS_SLUZBY)}>
                                <TranslateText>POPIS_SLUZBY</TranslateText>.
                            </a>
                        </div>
                        <div
                            style={{
                                display: 'block',
                                textDecoration: 'underline',
                                marginTop: 6
                            }}
                        >
                            <a
                                href={`https://${window.location.host}/lei/sluzby-lei/historie-vydanych-seznamu-lei-pre-lei/`}
                                target="_blank"
                            >
                                <TranslateText>HISTORIE_VYDANNYCH_LEI</TranslateText>.
                            </a>
                        </div>
                    </>
                )}
            </FormWrapper>
        </LoadingWrapper>
    );
};

export default PrideleneLeiPrehled;
